"use client";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  startTransition,
} from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { ConfirmationDialog } from "../ui/confirmation-dialog";
import { getCompanyName } from "@/lib/textron-data-processor";
import { Progress } from "../ui/progress";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";

import {
  CheckCircle2,
  FileText,
  Image as ImageIcon,
  Loader2,
  Trash2,
  Download,
  ExternalLink,
  Lightbulb,
  XCircle,
  MessageSquare,
} from "lucide-react";
import { DocumentManager } from "./DocumentManager";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { ReviewComment } from "../shared/ReviewComment";

function UploadInline({
  sectionKey,
  groupKey,
  itemId,
  itemQuestion,
  buttonLabel = "Attach documents",
  compact = false,
}: {
  sectionKey: string;
  groupKey?: string;
  itemId: string;
  itemQuestion?: string;
  buttonLabel?: string;
  compact?: boolean;
}) {
  const { isArchivalMode } = useArchivalMode();
  const [uploads, setUploads] = useState<
    Record<
      string,
      {
        id: string;
        fileName: string;
        fileSize?: number;
        extension?: string;
        progress: number;
        status: "uploading" | "success" | "error";
        error?: string;
        previewUrl?: string;
        temp?: boolean;
      }
    >
  >({});

  const formatSize = (bytes?: number) => {
    if (!bytes && bytes !== 0) return "";
    const units = ["B", "KB", "MB", "GB"];
    let size = bytes;
    let u = 0;
    while (size > 1024 && u < units.length - 1) {
      size /= 1024;
      u++;
    }
    return `${size.toFixed(1)} ${units[u]}`;
  };

  const iconForExt = (ext?: string) => {
    const e = (ext || "").toLowerCase();
    if ([".png", ".jpg", ".jpeg"].includes(e))
      return <ImageIcon className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  useEffect(() => {
    const off = (window as any).documents.onProgress(
      (payload: {
        id: string;
        progress: number;
        status: "uploading" | "success" | "error";
        error?: string;
        fileName?: string;
        fileSize?: number;
        extension?: string;
        category?: string;
        contextId?: string;
        contextLabel?: string;
        groupKey?: string;
      }) => {
        if (
          payload.category !== sectionKey ||
          payload.contextId !== itemId ||
          payload.groupKey !== groupKey
        ) {
          return;
        }
        setUploads((prev) => {
          const temps = Object.entries(prev).filter(
            ([, v]) =>
              v.temp &&
              v.fileName === payload.fileName &&
              v.fileSize === payload.fileSize,
          );
          let next = { ...prev } as typeof prev;
          if (temps.length > 0) {
            const [, tempVal] = temps[0];
            delete next[tempVal.id];
            next[payload.id] = {
              id: payload.id,
              fileName: payload.fileName || tempVal.fileName,
              fileSize: payload.fileSize ?? tempVal.fileSize,
              extension: payload.extension ?? tempVal.extension,
              progress: payload.progress,
              status: payload.status,
              error: payload.error,
              previewUrl: tempVal.previewUrl,
            };
          } else {
            next[payload.id] = {
              id: payload.id,
              fileName: payload.fileName || payload.id,
              fileSize: payload.fileSize,
              extension: payload.extension,
              progress: payload.progress,
              status: payload.status,
              error: payload.error,
            };
          }
          return next;
        });
      },
    );
    return () => off && off();
  }, [sectionKey, groupKey, itemId]);

  const addPendingFromFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const now = Date.now();
    const entries: Record<string, any> = {};
    Array.from(fileList).forEach((f, idx) => {
      const id = `temp-${now}-${idx}`;
      const name = (f as File).name;
      const size = (f as File).size;
      const dot = name.lastIndexOf(".");
      const ext = dot >= 0 ? name.slice(dot).toLowerCase() : "";
      let previewUrl: string | undefined;
      if ([".png", ".jpg", ".jpeg"].includes(ext)) {
        try {
          previewUrl = URL.createObjectURL(f as File);
        } catch {}
      }
      entries[id] = {
        id,
        fileName: name,
        fileSize: size,
        extension: ext,
        progress: 0,
        status: "uploading" as const,
        previewUrl,
        temp: true,
      };
    });
    setUploads((prev) => ({ ...prev, ...entries }));
  };

  const handlePickFiles = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = (await (window as any).documents.acceptedTypes()).join(",");
    input.onchange = async () => {
      const files = (input.files || []) as unknown as FileList;
      try {
        await addPendingFromFiles(files);
      } catch {}
      const paths: string[] = [];
      for (const f of Array.from(files)) {
        const fwp = f as unknown as { path?: string };
        if (fwp.path) paths.push(fwp.path);
      }
      if (paths.length) {
        const label = `${itemId}${itemQuestion ? ` • ${itemQuestion}` : ""}`;
        await (window as any).documents.upload(paths, sectionKey, {
          contextId: itemId,
          contextLabel: label,
          groupKey,
        });
      }
    };
    input.click();
  };

  const items = Object.values(uploads);

  return (
    <div className={compact ? "" : "space-y-2"}>
      <button
        type="button"
        className={
          compact
            ? "rounded bg-white/10 px-2 py-1 text-[11px] text-white hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
            : "h-10 w-full rounded-md border border-white/10 bg-white/10 text-xs text-white hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
        }
        onClick={handlePickFiles}
        disabled={isArchivalMode}
      >
        {buttonLabel}
      </button>
      {items.length > 0 && (
        <div className="mt-2 space-y-2">
          {items.map((u) => (
            <div
              key={u.id}
              className="animate-in fade-in slide-in-from-top-1 rounded-md border border-white/10 bg-white/5 p-2"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded bg-white/10">
                  {[".png", ".jpg", ".jpeg"].includes(
                    (u.extension || "").toLowerCase(),
                  ) && u.previewUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={u.previewUrl}
                      alt={u.fileName}
                      className="h-10 w-10 object-cover"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  ) : (
                    <div className="text-white/70">
                      {iconForExt(u.extension)}
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div
                    className="truncate text-xs text-white/90"
                    title={u.fileName}
                  >
                    {u.fileName}
                  </div>
                  <div className="text-[10px] text-white/50">
                    {formatSize(u.fileSize)} •{" "}
                    {u.status === "success" ? (
                      <span className="text-emerald-400">Loaded</span>
                    ) : u.status === "error" ? (
                      <span className="text-red-400">Error</span>
                    ) : (
                      <span className="text-white/70">Processing…</span>
                    )}
                  </div>
                  {u.status === "uploading" && (
                    <div className="mt-1">
                      <Progress value={u.progress} />
                    </div>
                  )}
                </div>
                <div className="shrink-0">
                  {u.status === "success" ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                  ) : u.status === "error" ? (
                    <XCircle className="h-4 w-4 text-red-400" />
                  ) : (
                    <Loader2 className="h-4 w-4 animate-spin text-white/70" />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

type SectionKey =
  | "engagement-acceptance"
  | "fraud-risk"
  | "it-risk"
  | "materiality"
  | "preliminary-analytical"
  | "understanding-entity";

interface PlanningSectionProps {
  sectionKey: SectionKey;
  title: string;
  initialTab?: string;
}

type JsonValue = any;

function getTopLevelTabsForContent(
  content: JsonValue,
): Array<{ key: string; title: string; items?: any[] }> {
  // Each JSON has a single top-level key mapping to either an array or grouped sections
  const rootKeys = Object.keys(content || {});
  if (rootKeys.length === 0) return [];
  const firstKey = rootKeys[0];
  const root = content[firstKey];

  if (Array.isArray(root)) {
    return [{ key: firstKey, title: firstKey, items: root }];
  }
  if (typeof root === "object" && root) {
    return Object.entries(root).map(([k, v]) => ({
      key: k,
      title: k,
      items: Array.isArray(v) ? (v as any[]) : undefined,
    }));
  }
  return [{ key: firstKey, title: firstKey, items: [] }];
}

function SimpleTableEditor({
  rows,
  onChange,
}: {
  rows: any[] | undefined;
  onChange: (next: any[]) => void;
}) {
  const value = Array.isArray(rows) ? rows : [];
  return (
    <div className="space-y-2">
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => onChange([...value, ""])}
      >
        Add Row
      </Button>
      <div className="space-y-2">
        {value.map((row, idx) => (
          <div key={idx} className="flex gap-2">
            <Input
              value={row ?? ""}
              onChange={(e) => {
                const next = [...value];
                next[idx] = e.target.value;
                onChange(next);
              }}
              className="text-white"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                const next = [...value];
                next.splice(idx, 1);
                onChange(next);
              }}
            >
              Remove
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

interface CustomerSupplierRow {
  typeOfRelationship: string;
  nameOfParty: string;
  whetherRelated: string;
  riskAssessed: string;
}

interface FraudInquiryRow {
  role: string;
  name: string;
  position: string;
  date: string;
}

interface FRA12InquiryRow {
  role: string;
  name: string;
  position: string;
  date: string;
}

interface PD3PersonnelRow {
  nameOfPersonnel: string;
  position: string;
}

interface AccountingEstimateRow {
  accounting_estimate: string;
  fs_line_item: string;
  description: string;
}

function FraudInquiryTable({
  value,
  onChange,
  disabled = false,
}: {
  value: FraudInquiryRow[] | undefined;
  onChange: (next: FraudInquiryRow[]) => void;
  disabled?: boolean;
}) {
  const rows = Array.isArray(value) ? value : [];

  const addRow = () => {
    onChange([
      ...rows,
      {
        role: "",
        name: "",
        position: "",
        date: "",
      },
    ]);
  };

  const updateRow = (index: number, field: keyof FraudInquiryRow, newValue: string) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: newValue };
    onChange(updated);
  };

  const removeRow = (index: number) => {
    const updated = rows.filter((_, i) => i !== index);
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-white/10 bg-black/20">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-white/5">
              <TableHead className="text-white/80">Role</TableHead>
              <TableHead className="text-white/80">Name</TableHead>
              <TableHead className="text-white/80">Position</TableHead>
              <TableHead className="text-white/80">Date of Discussion</TableHead>
              <TableHead className="w-[100px] text-white/80">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-white/60">
                  No rows added. Click "Add Row" to add a new entry.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={index} className="border-white/10 hover:bg-white/5">
                  <TableCell>
                    <Select
                      value={row.role}
                      onValueChange={(val) => updateRow(index, "role", val)}
                      disabled={disabled}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Management">Management</SelectItem>
                        <SelectItem value="Other's within the entity">Other's within the entity</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Input
                      value={row.name}
                      onChange={(e) => updateRow(index, "name", e.target.value)}
                      disabled={disabled}
                      className="text-white"
                      placeholder="Enter name"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={row.position}
                      onChange={(e) => updateRow(index, "position", e.target.value)}
                      disabled={disabled}
                      className="text-white"
                      placeholder="Enter position"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="date"
                      value={row.date ?? ""}
                      onChange={(e) => updateRow(index, "date", e.target.value)}
                      disabled={disabled}
                      className="text-white bg-black/40 border-white/10"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => removeRow(index)}
                      disabled={disabled}
                      className="text-white/80 hover:text-white"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={addRow}
        disabled={disabled}
        className="text-white/80 hover:text-white"
      >
        Add Row
      </Button>
    </div>
  );
}

function FRA12InquiryTable({
  value,
  onChange,
  disabled = false,
}: {
  value: FRA12InquiryRow[] | undefined;
  onChange: (next: FRA12InquiryRow[]) => void;
  disabled?: boolean;
}) {
  const rows = Array.isArray(value) ? value : [];

  const addRow = () => {
    onChange([
      ...rows,
      {
        role: "",
        name: "",
        position: "",
        date: "",
      },
    ]);
  };

  const updateRow = (index: number, field: keyof FRA12InquiryRow, newValue: string) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: newValue };
    onChange(updated);
  };

  const removeRow = (index: number) => {
    const updated = rows.filter((_, i) => i !== index);
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-white/10 bg-black/20">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-white/5">
              <TableHead className="text-white/80">Role</TableHead>
              <TableHead className="text-white/80">Name</TableHead>
              <TableHead className="text-white/80">Position</TableHead>
              <TableHead className="text-white/80">Date of Discussion</TableHead>
              <TableHead className="w-[100px] text-white/80">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-white/60">
                  No rows added. Click "Add Row" to add a new entry.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={index} className="border-white/10 hover:bg-white/5">
                  <TableCell>
                    <Select
                      value={row.role}
                      onValueChange={(val) => updateRow(index, "role", val)}
                      disabled={disabled}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Management">Management</SelectItem>
                        <SelectItem value="Other's within the entity">Other's within the entity</SelectItem>
                        <SelectItem value="Those Charged with Governance">Those Charged with Governance</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Input
                      value={row.name}
                      onChange={(e) => updateRow(index, "name", e.target.value)}
                      disabled={disabled}
                      className="text-white"
                      placeholder="Enter name"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={row.position}
                      onChange={(e) => updateRow(index, "position", e.target.value)}
                      disabled={disabled}
                      className="text-white"
                      placeholder="Enter position"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="date"
                      value={row.date ?? ""}
                      onChange={(e) => updateRow(index, "date", e.target.value)}
                      disabled={disabled}
                      className="text-white bg-black/40 border-white/10"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => removeRow(index)}
                      disabled={disabled}
                      className="text-white/80 hover:text-white"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={addRow}
        disabled={disabled}
        className="text-white/80 hover:text-white"
      >
        Add Row
      </Button>
    </div>
  );
}

function PD3PersonnelTable({
  value,
  onChange,
  disabled = false,
}: {
  value: PD3PersonnelRow[] | undefined;
  onChange: (next: PD3PersonnelRow[]) => void;
  disabled?: boolean;
}) {
  const rows = Array.isArray(value) ? value : [];

  const addRow = () => {
    onChange([
      ...rows,
      {
        nameOfPersonnel: "",
        position: "",
      },
    ]);
  };

  const updateRow = (index: number, field: keyof PD3PersonnelRow, newValue: string) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: newValue };
    onChange(updated);
  };

  const removeRow = (index: number) => {
    const updated = rows.filter((_, i) => i !== index);
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-white/10 bg-black/20">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-white/5">
              <TableHead className="text-white/80">Name of Personnel</TableHead>
              <TableHead className="text-white/80">Position</TableHead>
              <TableHead className="w-[100px] text-white/80">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="py-8 text-center text-white/60">
                  No rows added. Click "Add Row" to add a new entry.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={index} className="border-white/10 hover:bg-white/5">
                  <TableCell>
                    <Input
                      value={row.nameOfPersonnel}
                      onChange={(e) => updateRow(index, "nameOfPersonnel", e.target.value)}
                      disabled={disabled}
                      className="text-white"
                      placeholder="Enter name"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={row.position}
                      onChange={(e) => updateRow(index, "position", e.target.value)}
                      disabled={disabled}
                      className="text-white"
                      placeholder="Enter position"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => removeRow(index)}
                      disabled={disabled}
                      className="text-white/80 hover:text-white"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={addRow}
        disabled={disabled}
        className="text-white/80 hover:text-white"
      >
        Add Row
      </Button>
    </div>
  );
}

function CustomerSupplierTable({
  value,
  onChange,
  disabled = false,
}: {
  value: CustomerSupplierRow[] | undefined;
  onChange: (next: CustomerSupplierRow[]) => void;
  disabled?: boolean;
}) {
  const rows = Array.isArray(value) ? value : [];

  const addRow = () => {
    onChange([
      ...rows,
      {
        typeOfRelationship: "",
        nameOfParty: "",
        whetherRelated: "",
        riskAssessed: "",
      },
    ]);
  };

  const updateRow = (index: number, field: keyof CustomerSupplierRow, newValue: string) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: newValue };
    onChange(updated);
  };

  const removeRow = (index: number) => {
    const updated = rows.filter((_, i) => i !== index);
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-white/10 bg-black/20">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-white/5">
              <TableHead className="text-white/80">Type of Relationship</TableHead>
              <TableHead className="text-white/80">Name of Party</TableHead>
              <TableHead className="text-white/80">Whether Related Party?</TableHead>
              <TableHead className="text-white/80">Risk Assessed</TableHead>
              <TableHead className="w-[100px] text-white/80">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-white/60">
                  No rows added. Click "Add Row" to add a new entry.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={index} className="border-white/10 hover:bg-white/5">
                  <TableCell>
                    <Select
                      value={row.typeOfRelationship}
                      onValueChange={(val) => updateRow(index, "typeOfRelationship", val)}
                      disabled={disabled}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Customer">Customer</SelectItem>
                        <SelectItem value="Supplier">Supplier</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Input
                      value={row.nameOfParty}
                      onChange={(e) => updateRow(index, "nameOfParty", e.target.value)}
                      disabled={disabled}
                      className="text-white"
                      placeholder="Enter party name"
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      value={row.whetherRelated}
                      onValueChange={(val) => updateRow(index, "whetherRelated", val)}
                      disabled={disabled}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={row.riskAssessed}
                      onValueChange={(val) => updateRow(index, "riskAssessed", val)}
                      disabled={disabled}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Lower">Lower</SelectItem>
                        <SelectItem value="Higher">Higher</SelectItem>
                        <SelectItem value="Significant">Significant</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => removeRow(index)}
                      disabled={disabled}
                      className="text-white/80 hover:text-white"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={addRow}
        disabled={disabled}
        className="text-white/80 hover:text-white"
      >
        Add Row
      </Button>
    </div>
  );
}

function AccountingEstimateTable({
  value,
  onChange,
  disabled = false,
}: {
  value: AccountingEstimateRow[] | undefined;
  onChange: (next: AccountingEstimateRow[]) => void;
  disabled?: boolean;
}) {
  const rows = Array.isArray(value) ? value : [];

  const addRow = () => {
    onChange([
      ...rows,
      {
        accounting_estimate: "",
        fs_line_item: "",
        description: "",
      },
    ]);
  };

  const updateRow = (index: number, field: keyof AccountingEstimateRow, newValue: string) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: newValue };
    onChange(updated);
  };

  const removeRow = (index: number) => {
    const updated = rows.filter((_, i) => i !== index);
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-white/10 bg-black/20">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-white/5">
              <TableHead className="text-white/80">Accounting Estimate</TableHead>
              <TableHead className="text-white/80">FS Line Item</TableHead>
              <TableHead className="text-white/80">Description</TableHead>
              <TableHead className="w-[100px] text-white/80">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="py-8 text-center text-white/60">
                  No rows added. Click "Add Row" to add a new entry.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={index} className="border-white/10 hover:bg-white/5">
                  <TableCell>
                    <Input
                      value={row.accounting_estimate}
                      onChange={(e) => updateRow(index, "accounting_estimate", e.target.value)}
                      disabled={disabled}
                      className="text-white"
                      placeholder="Enter accounting estimate"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={row.fs_line_item}
                      onChange={(e) => updateRow(index, "fs_line_item", e.target.value)}
                      disabled={disabled}
                      className="text-white"
                      placeholder="Enter FS line item"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={row.description}
                      onChange={(e) => updateRow(index, "description", e.target.value)}
                      disabled={disabled}
                      className="text-white"
                      placeholder="Enter description"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => removeRow(index)}
                      disabled={disabled}
                      className="text-white/80 hover:text-white"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={addRow}
        disabled={disabled}
        className="text-white/80 hover:text-white"
      >
        Add Row
      </Button>
    </div>
  );
}

function FieldRenderer({
  item,
  value,
  onChange,
  sectionKey,
  groupKey,
  disabled = false,
  isEditMode = true,
  setItemField,
}: {
  item: any;
  value: any;
  onChange: (val: any) => void;
  sectionKey: SectionKey;
  groupKey?: string;
  disabled?: boolean;
  isEditMode?: boolean;
  setItemField?: (groupKey: string, itemId: string, fieldName: string, nextValue: any, mergeObject?: boolean) => void;
}) {
  const type = item.type as string;
  
  // Debug logging for FRA-5 and FRA-12
  if (item.id === "FRA-5" || item.id === "FRA-12") {
    console.log(`[${item.id}] FieldRenderer called:`, {
      itemId: item.id,
      type,
      typeValue: item.type,
      hasGroupKey: !!groupKey,
      groupKey,
      itemKeys: Object.keys(item),
      willMatchTableCase: type === "table"
    });
  }
  
  const [uploads, setUploads] = useState<
    Record<
      string,
      {
        id: string;
        fileName: string;
        fileSize?: number;
        extension?: string;
        progress: number;
        status: "uploading" | "success" | "error";
        error?: string;
        previewUrl?: string;
        temp?: boolean;
      }
    >
  >({});

  const formatSize = (bytes?: number) => {
    if (!bytes && bytes !== 0) return "";
    const units = ["B", "KB", "MB", "GB"];
    let size = bytes;
    let u = 0;
    while (size > 1024 && u < units.length - 1) {
      size /= 1024;
      u++;
    }
    return `${size.toFixed(1)} ${units[u]}`;
  };

  const iconForExt = (ext?: string) => {
    const e = (ext || "").toLowerCase();
    if ([".png", ".jpg", ".jpeg"].includes(e))
      return <ImageIcon className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  // Subscribe to global upload progress, filter to this question context
  useEffect(() => {
    const off = (window as any).documents.onProgress(
      (payload: {
        id: string;
        progress: number;
        status: "uploading" | "success" | "error";
        error?: string;
        fileName?: string;
        fileSize?: number;
        extension?: string;
        category?: string;
        contextId?: string;
        contextLabel?: string;
        groupKey?: string;
      }) => {
        if (
          payload.category !== sectionKey ||
          payload.contextId !== item.id ||
          payload.groupKey !== groupKey
        ) {
          return;
        }
        setUploads((prev) => {
          // Try to reconcile any temp entries by name+size
          const temps = Object.entries(prev).filter(
            ([, v]) =>
              v.temp &&
              v.fileName === payload.fileName &&
              v.fileSize === payload.fileSize,
          );
          let next = { ...prev };
          if (temps.length > 0) {
            const [, tempVal] = temps[0];
            delete next[tempVal.id];
            next[payload.id] = {
              id: payload.id,
              fileName: payload.fileName || tempVal.fileName,
              fileSize: payload.fileSize ?? tempVal.fileSize,
              extension: payload.extension ?? tempVal.extension,
              progress: payload.progress,
              status: payload.status,
              error: payload.error,
              previewUrl: tempVal.previewUrl,
            };
          } else {
            next[payload.id] = {
              id: payload.id,
              fileName: payload.fileName || payload.id,
              fileSize: payload.fileSize,
              extension: payload.extension,
              progress: payload.progress,
              status: payload.status,
              error: payload.error,
            };
          }
          return next;
        });
      },
    );
    return () => off && off();
  }, [sectionKey, groupKey, item.id]);

  const addPendingFromFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const now = Date.now();
    const entries: Record<string, any> = {};
    Array.from(fileList).forEach((f, idx) => {
      const id = `temp-${now}-${idx}`;
      const name = (f as File).name;
      const size = (f as File).size;
      const dot = name.lastIndexOf(".");
      const ext = dot >= 0 ? name.slice(dot).toLowerCase() : "";
      let previewUrl: string | undefined;
      if ([".png", ".jpg", ".jpeg"].includes(ext)) {
        try {
          previewUrl = URL.createObjectURL(f as File);
        } catch {}
      }
      entries[id] = {
        id,
        fileName: name,
        fileSize: size,
        extension: ext,
        progress: 0,
        status: "uploading" as const,
        previewUrl,
        temp: true,
      };
    });
    setUploads((prev) => ({ ...prev, ...entries }));
  };

  const renderUploadIndicators = () => {
    const list = Object.values(uploads);
    if (list.length === 0) return null;
    return (
      <div className="mt-2 space-y-2">
        {list.map((u) => (
          <div
            key={u.id}
            className="animate-in fade-in slide-in-from-top-1 rounded-md border border-white/10 bg-white/5 p-2"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded bg-white/10">
                {[".png", ".jpg", ".jpeg"].includes(
                  (u.extension || "").toLowerCase(),
                ) && u.previewUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={u.previewUrl}
                    alt={u.fileName}
                    className="h-10 w-10 object-cover"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                ) : (
                  <div className="text-white/70">{iconForExt(u.extension)}</div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div
                  className="truncate text-xs text-white/90"
                  title={u.fileName}
                >
                  {u.fileName}
                </div>
                <div className="text-[10px] text-white/50">
                  {formatSize(u.fileSize)} •{" "}
                  {u.status === "success" ? (
                    <span className="text-emerald-400">Loaded</span>
                  ) : u.status === "error" ? (
                    <span className="text-red-400">Error</span>
                  ) : (
                    <span className="text-white/70">Processing…</span>
                  )}
                </div>
                {u.status === "uploading" && (
                  <div className="mt-1">
                    <Progress value={u.progress} />
                  </div>
                )}
              </div>
              <div className="shrink-0">
                {u.status === "success" ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                ) : u.status === "error" ? (
                  <XCircle className="h-4 w-4 text-red-400" />
                ) : (
                  <Loader2 className="h-4 w-4 animate-spin text-white/70" />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  // Debug: Log switch statement entry for table types
  if (item.id === "FRA-5" || item.id === "FRA-12") {
    console.log(`[${item.id}] Entering switch statement with type:`, type, `(typeof: ${typeof type})`);
  }
  
  switch (type) {
    case "table":
      // Debug: Log table case entry
      if (item.id === "FRA-5" || item.id === "FRA-12") {
        console.log(`[${item.id}] Table case matched!`);
      }
      // Handle specific table types
      if (item.id === "PD-3") {
        return (
          <PD3PersonnelTable
            value={item.pd3PersonnelTable}
            onChange={(val) => {
              if (groupKey) {
                setItemField?.(groupKey, item.id, "pd3PersonnelTable", val);
              }
            }}
            disabled={disabled}
          />
        );
      } else if (item.id === "FRA-5") {
        // FRA-5 only needs table (no DocumentManager)
        console.log('[FRA-5] Rendering FraudInquiryTable:', {
          itemId: item.id,
          hasGroupKey: !!groupKey,
          groupKey,
          hasRelatedPartiesTable: !!item.relatedPartiesTable,
          tableData: item.relatedPartiesTable
        });
        return (
          <div>
            <div className="mb-2 text-xs font-medium text-white/80">
              Details
            </div>
            <FraudInquiryTable
              value={item.relatedPartiesTable}
              onChange={(val) => {
                console.log('[FRA-5] Table onChange:', { groupKey, itemId: item.id, val });
                if (groupKey) {
                  setItemField?.(groupKey, item.id, "relatedPartiesTable", val);
                } else {
                  console.warn('[FRA-5] No groupKey available, cannot save table data');
                }
              }}
              disabled={disabled}
            />
          </div>
        );
      } else if (item.id === "FRA-12") {
        console.log('[FRA-12] Rendering FRA12InquiryTable:', {
          itemId: item.id,
          hasGroupKey: !!groupKey,
          groupKey,
          hasFra12InquiryTable: !!item.fra12InquiryTable,
          tableData: item.fra12InquiryTable
        });
        return (
          <FRA12InquiryTable
            value={item.fra12InquiryTable}
            onChange={(val) => {
              console.log('[FRA-12] Table onChange:', { groupKey, itemId: item.id, val });
              if (groupKey) {
                setItemField?.(groupKey, item.id, "fra12InquiryTable", val);
              } else {
                console.warn('[FRA-12] No groupKey available, cannot save table data');
              }
            }}
            disabled={disabled}
          />
        );
      }
      return <SimpleTableEditor rows={value} onChange={onChange} />;

    case "radio":
      return (
        <div className="flex flex-wrap gap-2">
          {(item.options || []).map((opt: string) => (
            <Button
              key={opt}
              type="button"
              variant={value === opt ? "default" : "outline"}
              className="h-8 px-3"
              onClick={() => onChange(opt)}
              disabled={disabled}
            >
              {opt}
            </Button>
          ))}
        </div>
      );
    case "text":
    case "percent":
      return (
        <Input
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter value"
          className="text-white"
          disabled={disabled}
        />
      );
    case "date":
      return (
        <Input
          type="date"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="text-white bg-black/40 border-white/10"
        />
      );
    case "dropdown":
      return (
        <select
          className="rounded-md border border-white/10 bg-black/40 px-2 py-1 text-white"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        >
          <option value="" disabled>
            Select
          </option>
          {(item.options || []).map((o: string) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    case "multi-select":
      return (
        <div className="flex flex-wrap gap-2">
          {(item.options || []).map((opt: string) => {
            const selected = Array.isArray(value) && value.includes(opt);
            return (
              <Button
                key={opt}
                type="button"
                variant={selected ? "default" : "outline"}
                className="h-8 px-3"
                onClick={() => {
                  let next = Array.isArray(value) ? [...value] : [];
                  if (selected) next = next.filter((v) => v !== opt);
                  else next.push(opt);
                  onChange(next);
                }}
                disabled={disabled}
              >
                {opt}
              </Button>
            );
          })}
        </div>
      );
    case "document":
      return (
        <div className="space-y-3">
          {Array.isArray(item.subInputs) && item.subInputs.length > 0 && (
            <div className="space-y-2">
              {item.subInputs.map((si: any) => (
                <div key={si.label} className="flex items-center gap-3">
                  <div className="min-w-48 text-sm text-white/80">
                    {si.label}
                  </div>
                  <select
                    className="rounded-md border border-white/10 bg-black/40 px-2 py-1 text-white"
                    value={value?.[si.label] ?? ""}
                    onChange={(e) =>
                      onChange({ ...(value ?? {}), [si.label]: e.target.value })
                    }
                    disabled={disabled}
                  >
                    <option value="" disabled>
                      Select
                    </option>
                    {(si.options || []).map((o: string) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}
          <DocumentManager
            sectionKey={String(sectionKey)}
            groupKey={groupKey}
            itemId={String(item.id)}
            itemQuestion={String(item.question || "")}
            itemTextResponse={item.textResponse}
            itemDocuments={item.documents}
            disabled={!isEditMode}
            onDocumentsChange={(documents) => {
              // Update the item's documents field
              if (groupKey) {
                setItemField?.(groupKey, item.id, "documents", documents);
              }
            }}
            onTextResponseChange={(textResponse) => {
              // Update the item's textResponse field
              if (groupKey) {
                setItemField?.(groupKey, item.id, "textResponse", textResponse);
              }
            }}
          />
        </div>
      );
    default:
      // Check for generic table flag as fallback
      if (item.table === true) {
        return <SimpleTableEditor rows={value} onChange={onChange} />;
      }
      return (
        <div className="text-white/60">
          Unsupported field type: {String(type)}
        </div>
      );
  }
}

function DocumentsPanel({ currentCategory }: { currentCategory: string }) {
  const { isArchivalMode } = useArchivalMode();
  const [docs, setDocs] = useState<DocumentMetadata[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await (window as any).documents.list();
      setDocs(Array.isArray(list) ? list : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const off = (window as any).documents.onProgress((p: any) => {
      if (p?.status === "success") load();
    });
    return () => off && off();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return docs;
    return docs.filter((d) => {
      return (
        d.fileName.toLowerCase().includes(q) ||
        (d.contextLabel || "").toLowerCase().includes(q) ||
        d.category.toLowerCase().includes(q)
      );
    });
  }, [docs, search]);

  const grouped = useMemo(() => {
    const map = new Map<string, DocumentMetadata[]>();
    for (const d of filtered) {
      const arr = map.get(d.category) || [];
      arr.push(d);
      map.set(d.category, arr);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const iconFor = (ext: string) => {
    const e = (ext || "").toLowerCase();
    if ([".png", ".jpg", ".jpeg"].includes(e))
      return <ImageIcon className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  const filePreview = (doc: DocumentMetadata) => {
    const e = (doc.extension || "").toLowerCase();
    if ([".png", ".jpg", ".jpeg"].includes(e)) {
      const src = `file://${doc.thumbnailPath || ""}`;
      return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={src}
          alt={doc.fileName}
          className="h-9 w-9 rounded object-cover"
          onError={(e) => (e.currentTarget.style.display = "none")}
        />
      );
    }
    return (
      <div className="flex h-9 w-9 items-center justify-center rounded bg-white/10 text-white/70">
        {iconFor(doc.extension)}
      </div>
    );
  };

  const formatDate = (ts: number) => new Date(ts).toLocaleString();
  const formatSize = (bytes: number) => {
    const units = ["B", "KB", "MB", "GB"];
    let size = bytes;
    let u = 0;
    while (size > 1024 && u < units.length - 1) {
      size /= 1024;
      u++;
    }
    return `${size.toFixed(1)} ${units[u]}`;
  };

  const handleDelete = async (id: string) => {
    const res = await (window as any).documents.delete(id);
    if (res?.ok) setDocs((prev) => prev.filter((d) => d.id !== id));
  };
  const handleOpen = async (id: string) => {
    await (window as any).documents.open(id);
  };
  const handleDownload = async (id: string) => {
    await (window as any).documents.download(id);
  };

  const handleUploadPick = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    try {
      const acc = await (window as any).documents.acceptedTypes();
      input.accept = Array.isArray(acc) ? acc.join(",") : "";
    } catch {}
    input.onchange = async () => {
      const files = (input.files || []) as unknown as FileList;
      const paths: string[] = [];
      for (const f of Array.from(files)) {
        const fwp = f as unknown as { path?: string };
        if (fwp.path) paths.push(fwp.path);
      }
      if (paths.length) {
        await (window as any).documents.upload(paths, currentCategory);
        await load();
      }
    };
    input.click();
  };

  return (
    <div className="rounded-lg border border-white/10 bg-black/25 p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-medium text-white">Documents</div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="border-white/10 text-xs text-white/80 hover:bg-white/10 hover:text-white"
          onClick={handleUploadPick}
        >
          Upload to "{currentCategory}"
        </Button>
      </div>
      <Input
        placeholder="Search documents"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-2 text-white"
      />
      {loading ? (
        <div className="py-6 text-center text-white/60">Loading…</div>
      ) : grouped.length === 0 ? (
        <div className="py-6 text-center text-white/60">No documents yet.</div>
      ) : (
        <div className="space-y-4">
          {grouped.map(([cat, list]) => (
            <div key={cat}>
              <div className="mb-2 text-xs font-semibold tracking-wide text-white/60 uppercase">
                {cat}
              </div>
              <ul className="space-y-2">
                {list
                  .slice()
                  .sort((a, b) => b.uploadTimestamp - a.uploadTimestamp)
                  .map((doc) => (
                    <li
                      key={doc.id}
                      className="group flex items-center justify-between rounded-lg border border-white/10 bg-white/5 p-2 hover:bg-white/10"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        {filePreview(doc)}
                        <div className="min-w-0">
                          <button
                            className="truncate text-left text-sm font-medium text-white hover:underline"
                            onClick={() => handleOpen(doc.id)}
                            title={doc.fileName}
                          >
                            {doc.fileName}
                          </button>
                          <div className="truncate text-xs text-white/50">
                            {doc.contextLabel ? `${doc.contextLabel} • ` : ""}
                            {formatDate(doc.uploadTimestamp)} •{" "}
                            {formatSize(doc.fileSize)}
                          </div>
                        </div>
                      </div>
                      <div className="ml-2 flex items-center gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                        <button
                          className="rounded border border-white/10 p-1 text-white/70 hover:bg-white/10 hover:text-white"
                          title="Open"
                          onClick={() => handleOpen(doc.id)}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </button>
                        <button
                          className="rounded border border-white/10 p-1 text-white/70 hover:bg-white/10 hover:text-white"
                          title="Download"
                          onClick={() => handleDownload(doc.id)}
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        {!isArchivalMode && (
                          <button
                            className="rounded border border-white/10 p-1 text-red-400 hover:bg-white/10"
                            title="Delete"
                            onClick={() => handleDelete(doc.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PlanningSection({ sectionKey, title, initialTab: propInitialTab }: PlanningSectionProps) {
  const { isArchivalMode } = useArchivalMode();
  const [companyName, setCompanyName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState<JsonValue | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [activeTab, setActiveTab] = useState<string | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState("");
  const [onlyUnanswered, setOnlyUnanswered] = useState(false);
  const [sharePointSaveStatus, setSharePointSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [sharePointSaveError, setSharePointSaveError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [showStopEditingDialog, setShowStopEditingDialog] = useState<boolean>(false);
  const [reviewCommentState, setReviewCommentState] = useState<{
    questionId: string;
    questionText?: string;
    workspace?: string;
    subPhase?: string;
  } | null>(null);
  const questionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  
  // Prevent edit mode in archival mode
  useEffect(() => {
    if (isArchivalMode && isEditMode) {
      setIsEditMode(false);
    }
  }, [isArchivalMode, isEditMode]);

  // Load company name first
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");
      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  useEffect(() => {
    if (!companyName) return; // Don't proceed until company name is loaded
    
    let mounted = true;
    setLoading(true);
    
    // For all planning sections, try to load from SharePoint first, then fallback to local storage
    loadFromSharePoint().then((json: any) => {
      if (mounted) {
        setContent(json);
        setLoading(false);
      }
    }).catch(() => {
      // Fallback to local storage if SharePoint fails
      (window as any).planning
        .readSection(companyName, sectionKey)
        .then((json: any) => {
          if (mounted) {
            setContent(json);
            setLoading(false);
          }
        })
        .catch(() => setLoading(false));
    });
    
    return () => {
      mounted = false;
    };
  }, [companyName, sectionKey]);

  const tabs = useMemo(
    () => getTopLevelTabsForContent(content || {}),
    [content],
  );
  useEffect(() => {
    if (tabs.length && !activeTab) {
      // Use propInitialTab if provided and exists in tabs, otherwise use first tab
      const targetTab = propInitialTab && tabs.find(t => t.key === propInitialTab) 
        ? propInitialTab 
        : tabs[0].key;
      setActiveTab(targetTab);
    }
  }, [tabs, activeTab, propInitialTab]);

  const flattenItems = useCallback((items: any[]): any[] => {
    const out: any[] = [];
    for (const it of items) {
      if (it && typeof it === "object") {
        if (Array.isArray(it.questions)) {
          out.push(...flattenItems(it.questions));
        } else if (it.id) {
          out.push(it);
        }
      }
    }
    return out;
  }, []);

  const isValuePresent = (val: any): boolean => {
    if (val === null || typeof val === "undefined") return false;
    if (typeof val === "string") return val.trim().length > 0;
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === "object") return Object.keys(val).length > 0;
    return true;
  };

  const isItemAnswered = (item: any): boolean => {
    if (isValuePresent(item?.answer)) return true;

    const textResponse = item?.textResponse;
    const hasTextResponse =
      !!textResponse &&
      (
        (textResponse.html && String(textResponse.html).trim().length > 0) ||
        (textResponse.plainText && String(textResponse.plainText).trim().length > 0)
      );

    const hasDocuments =
      Array.isArray(item?.documents) && item.documents.length > 0;

    const hasTable =
      Array.isArray(item?.table) && item.table.length > 0;

    return !!(hasTextResponse || hasDocuments || hasTable);
  };

  const computeTabProgress = useCallback(
    (tabKey: string) => {
      const tab = tabs.find((t) => t.key === tabKey);
      const items = flattenItems(tab?.items || []);
      const total = items.length;
      const answered = items.filter((it) => isItemAnswered(it)).length;
      return { total, answered, items };
    },
    [tabs, flattenItems],
  );

  const scrollToQuestion = (id: string) => {
    const el = questionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  function updateInArray(
    arr: any[],
    targetId: string,
    mutate: (el: any) => { el: any; matched: boolean },
  ): { nextArr: any[]; found: boolean } {
    let found = false;
    const nextArr = arr.map((el) => {
      if (el && typeof el === "object") {
        if (el.id === targetId) {
          const res = mutate(el);
          if (res.matched) found = true;
          return res.el;
        }
        if (Array.isArray(el.questions)) {
          const { nextArr: qNext, found: qFound } = updateInArray(
            el.questions,
            targetId,
            mutate,
          );
          if (qFound) {
            found = true;
            return { ...el, questions: qNext };
          }
        }
      }
      return el;
    });
    return { nextArr, found };
  }

  // Use a ref to track the latest content for synchronous access during save
  const contentRef = useRef<JsonValue | null>(null);
  
  // Update ref whenever content changes
  useEffect(() => {
    contentRef.current = content;
  }, [content]);

  const setItemField = (
    groupKey: string,
    itemId: string,
    fieldName: string,
    nextValue: any,
    mergeObject = false,
  ) => {
    setContent((prev: JsonValue | null) => {
      if (!prev) return prev;
      const rootKey = Object.keys(prev)[0];
      const root = prev[rootKey];
      const next = { ...prev } as any;
      if (Array.isArray(root)) {
        const { nextArr } = updateInArray(root as any[], itemId, (el) => {
          const updated = mergeObject
            ? { ...el, [fieldName]: { ...(el[fieldName] || {}), ...nextValue } }
            : { ...el, [fieldName]: nextValue };
          return { el: updated, matched: true };
        });
        next[rootKey] = nextArr;
      } else {
        const groups = { ...(root as any) };
        const arr = Array.isArray(groups[groupKey]) ? groups[groupKey] : [];
        const { nextArr } = updateInArray(arr, itemId, (el) => {
          const updated = mergeObject
            ? { ...el, [fieldName]: { ...(el[fieldName] || {}), ...nextValue } }
            : { ...el, [fieldName]: nextValue };
          return { el: updated, matched: true };
        });
        groups[groupKey] = nextArr;
        next[rootKey] = groups;
      }
      
      // Update ref immediately with the new content
      contentRef.current = next;
      
      // For Engagement Acceptance, don't save locally - only save to SharePoint
      if (sectionKey === "engagement-acceptance") {
        // No local saving for Engagement Acceptance
        // All data will be saved to SharePoint only
      } else {
        // For other sections, continue with local saving
        (window as any).planning
          .saveSection(companyName, sectionKey, next)
          .then(() => setLastSaved(new Date()));
      }
      
      return next;
    });
  };

  const setItemAnswer = (groupKey: string, itemId: string, nextValue: any) => {
    // Use startTransition to mark this as non-urgent update to prevent UI blocking
    startTransition(() => {
      setItemField(groupKey, itemId, "answer", nextValue);
    });
  };

  // Function to load data from SharePoint for Engagement Acceptance
  const loadFromSharePoint = async () => {
    try {
      console.log(`[LOAD] Loading from SharePoint for section: ${sectionKey}, company: ${companyName}`);
      
      // Always use the local JSON file as the source of truth for question structure
      const baseContent = await (window as any).planning.readSection(companyName, sectionKey);
      console.log(`[LOAD] Base content loaded, keys:`, Object.keys(baseContent || {}));
      
      // Then, load SharePoint responses to merge only the answers
      if (window.planningAPI?.loadResponses) {
        console.log(`[LOAD] Calling planningAPI.loadResponses for section: ${sectionKey}`);
        const sharePointResponses = await window.planningAPI.loadResponses({
          companyName,
          sectionKey
        });
        
        console.log(`[LOAD] SharePoint response received:`, {
          success: sharePointResponses?.success,
          hasData: !!sharePointResponses?.data,
          dataKeys: sharePointResponses?.data ? Object.keys(sharePointResponses.data) : [],
          responseCount: sharePointResponses?.data ? Object.keys(sharePointResponses.data).length : 0
        });
        
        // Merge SharePoint responses into the base content (only answers, not question text)
        if (sharePointResponses && sharePointResponses.success && sharePointResponses.data) {
          console.log(`[LOAD] Merging SharePoint responses with local JSON structure for section: ${sectionKey}`);
          console.log(`[LOAD] Response IDs to merge:`, Object.keys(sharePointResponses.data));
          return mergeSharePointResponses(baseContent, sharePointResponses.data);
        } else {
          console.warn(`[LOAD] SharePoint responses not available or failed for section: ${sectionKey}`, sharePointResponses);
        }
      } else {
        console.warn(`[LOAD] planningAPI.loadResponses not available`);
      }
      
      // If SharePoint loading fails, return base content
      console.log(`[LOAD] Returning base content (no SharePoint data) for section: ${sectionKey}`);
      return baseContent;
    } catch (error) {
      console.error(`[LOAD] Failed to load from SharePoint for section ${sectionKey}:`, error);
      // Fallback to local content
      return await (window as any).planning.readSection(companyName, sectionKey);
    }
  };

  // Function to merge SharePoint responses into the base content structure
  const mergeSharePointResponses = (baseContent: any, sharePointResponses: Record<string, any>) => {
    const mergedContent = JSON.parse(JSON.stringify(baseContent)); // Deep clone
    
    console.log(`[MERGE] Starting merge for section: ${sectionKey}`);
    console.log(`[MERGE] Base content structure (local JSON) keys:`, Object.keys(baseContent || {}));
    console.log(`[MERGE] SharePoint responses to merge (${Object.keys(sharePointResponses || {}).length} items):`, Object.keys(sharePointResponses || {}));
    
    const applyResponses = (items: any[]) => {
      for (const item of items) {
        if (item.id && sharePointResponses[item.id]) {
          const response = sharePointResponses[item.id];
          
          console.log(`[MERGE] Merging response for ${item.id}:`, {
            originalQuestion: item.question,
            hasSelectedOption: !!response.selectedOption,
            hasTextResponse: !!response.textResponse,
            hasDocuments: !!response.documents,
            response: response
          });
          
          // Store original question text to preserve it
          const originalQuestion = item.question;
          
          // Apply the response based on the question type
          if (response.selectedOption !== null && response.selectedOption !== undefined) {
            item.answer = response.selectedOption;
          }
          
          if (response.textResponse) {
            // For text/textarea types, set answer directly
            if (item.type === "text" || item.type === "textarea") {
              item.answer = response.textResponse.plainText || response.textResponse.html || "";
            } else {
              // For other types (like document), set textResponse field
              // This will be used by DocumentManager to populate textbox
              item.textResponse = response.textResponse;
              
              // Also ensure documents array exists and has textbox entry if textResponse exists
              if (!item.documents) {
                item.documents = [];
              }
              // Find or create textbox document entry
              let textboxDoc = item.documents.find((doc: any) => doc.type === 'textbox');
              if (textboxDoc) {
                textboxDoc.content = response.textResponse.html || response.textResponse.plainText || "";
              } else {
                item.documents.push({
                  id: `textbox_${item.id}`,
                  type: 'textbox',
                  content: response.textResponse.html || response.textResponse.plainText || ""
                });
              }
            }
          }
          
          if (response.documents && response.documents.length > 0) {
            // Merge documents, preserving textbox if it exists
            const existingTextbox = item.documents?.find((doc: any) => doc.type === 'textbox');
            const fileDocuments = response.documents.filter((doc: any) => doc.type !== 'textbox');
            item.documents = existingTextbox ? [existingTextbox, ...fileDocuments] : fileDocuments;
          }
          
          // CRITICAL: Always preserve the original question text from local JSON
          item.question = originalQuestion;
          
          console.log(`Final question text for ${item.id}:`, item.question);
        }
        
        // Handle OC-4 CustomerSupplierTable loading (from within OC-4 response)
        if (item.id === "OC-4" && sharePointResponses[item.id] && sharePointResponses[item.id].CustomerSupplierTable) {
          const tableData = sharePointResponses[item.id].CustomerSupplierTable;
          if (tableData.rows && Array.isArray(tableData.rows) && tableData.rows.length > 0) {
            item.customerSupplierTable = tableData.rows;
            console.log(`Loaded CustomerSupplierTable for OC-4 with ${tableData.rows.length} rows`);
          }
        }

        // Handle OC-3 AccountingEstimateTable loading (from within OC-3 response)
        if (item.id === "OC-3" && sharePointResponses[item.id] && sharePointResponses[item.id].accounting_estimate) {
          const tableData = sharePointResponses[item.id].accounting_estimate;
          if (tableData.rows && Array.isArray(tableData.rows) && tableData.rows.length > 0) {
            // Convert rows from SharePoint format to component format
            item.accountingEstimateTable = tableData.rows.map((row: any) => ({
              accounting_estimate: row.accounting_estimate || "",
              fs_line_item: row.fs_line_item || "",
              description: row.description || ""
            }));
            console.log(`Loaded accounting_estimate table for OC-3 with ${tableData.rows.length} rows`);
          }
        }

        // Handle FRA-5 relatedPartiesTable loading (from within FRA-5 response)
        if (item.id === "FRA-5" && sharePointResponses[item.id] && sharePointResponses[item.id].relatedPartiesTable) {
          const tableData = sharePointResponses[item.id].relatedPartiesTable;
          if (tableData.rows && Array.isArray(tableData.rows) && tableData.rows.length > 0) {
            item.relatedPartiesTable = tableData.rows;
            console.log(`Loaded relatedPartiesTable for FRA-5 with ${tableData.rows.length} rows`);
          }
        }

        // Handle FRA-12 FRA12InquiryTable loading (from within FRA-12 response)
        if (item.id === "FRA-12" && sharePointResponses[item.id] && sharePointResponses[item.id].FRA12InquiryTable) {
          const tableData = sharePointResponses[item.id].FRA12InquiryTable;
          if (tableData.rows && Array.isArray(tableData.rows) && tableData.rows.length > 0) {
            item.fra12InquiryTable = tableData.rows;
            console.log(`Loaded FRA12InquiryTable for FRA-12 with ${tableData.rows.length} rows`);
          }
        }

        // Handle PD-3 PD3PersonnelTable loading (from within PD-3 response)
        if (item.id === "PD-3" && sharePointResponses[item.id] && sharePointResponses[item.id].PD3PersonnelTable) {
          const tableData = sharePointResponses[item.id].PD3PersonnelTable;
          if (tableData.rows && Array.isArray(tableData.rows) && tableData.rows.length > 0) {
            item.pd3PersonnelTable = tableData.rows;
            console.log(`Loaded PD3PersonnelTable for PD-3 with ${tableData.rows.length} rows`);
          }
        }

        
        // Recursively apply to nested questions
        if (item.questions && Array.isArray(item.questions)) {
          applyResponses(item.questions);
        }
      }
    };
    
    // Apply responses to all categories
    const rootKey = Object.keys(mergedContent)[0];
    const root = mergedContent[rootKey];
    
    console.log(`[MERGE] Processing root structure:`, {
      rootKey,
      rootType: Array.isArray(root) ? 'array' : typeof root,
      rootKeys: Array.isArray(root) ? `array with ${root.length} items` : Object.keys(root || {})
    });
    
    if (Array.isArray(root)) {
      console.log(`[MERGE] Root is array, applying responses to ${root.length} items`);
      applyResponses(root);
    } else {
      const categoryNames = Object.keys(root || {});
      console.log(`[MERGE] Root is object with ${categoryNames.length} categories:`, categoryNames);
      categoryNames.forEach(categoryName => {
        if (Array.isArray(root[categoryName])) {
          console.log(`[MERGE] Processing category "${categoryName}" with ${root[categoryName].length} items`);
          applyResponses(root[categoryName]);
        }
      });
    }
    
    console.log(`[MERGE] Merge complete for section: ${sectionKey}`);
    
    // Verify final question texts after merge
    console.log("Final merged content question texts:");
    console.log("AE-2 final question:", mergedContent["Engagement Acceptance & Continuance"]?.["Agreeing the Terms of Audit Engagement"]?.[1]?.question);
    console.log("AE-3 final question:", mergedContent["Engagement Acceptance & Continuance"]?.["Agreeing the Terms of Audit Engagement"]?.[2]?.question);
    
    return mergedContent;
  };


  // Function to save all responses to SharePoint for all planning sections
  const saveAllResponsesToSharePoint = async () => {
    if (!content) return;

    setSharePointSaveStatus('saving');
    setSharePointSaveError(null);

    try {
      // Auto-save any pending textbox content before processing responses
      console.log('Auto-saving textbox content...');
      
      // First, find all document managers and trigger their auto-save methods
      // This ensures all textbox content is saved to localStorage and state
      const documentManagers = document.querySelectorAll('[data-document-manager]');
      console.log('Found document managers:', documentManagers.length);
      
      // Trigger auto-save for all document managers first
      for (const manager of Array.from(documentManagers)) {
        const itemId = manager.getAttribute('data-item-id');
        if (itemId) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
          if (autoSaveMethod && typeof autoSaveMethod === 'function') {
            console.log(`[SAVE] Calling auto-save for item ${itemId}`);
            autoSaveMethod();
          }
        }
      }
      
      // Then, capture all contentEditable textbox editors and save their content directly to localStorage
      // This is a safety net to capture any content that might not have been saved yet
      const textboxEditors = document.querySelectorAll('[contenteditable="true"]');
      
      for (const editor of Array.from(textboxEditors)) {
        const htmlContent = (editor as HTMLElement).innerHTML;
        if (htmlContent && htmlContent.trim()) {
          // Try to find the itemId from the parent structure
          // The contentEditable is inside a DocumentManager which has data-document-manager attribute
          const documentManager = editor.closest('[data-document-manager]');
          if (documentManager) {
            const itemId = documentManager.getAttribute('data-item-id');
            if (itemId) {
              // Save directly to localStorage as immediate backup
              // This ensures we capture the current editor content even if state hasn't updated
              localStorage.setItem(`textbox_${itemId}`, htmlContent);
              console.log(`[SAVE] Saved textbox content directly from editor for item ${itemId}, length: ${htmlContent.length}`);
              
              // Also trigger auto-save again to ensure state is updated
              const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
              if (autoSaveMethod && typeof autoSaveMethod === 'function') {
                autoSaveMethod();
              }
            }
          }
        }
      }
      
      // Longer delay to ensure auto-save completes and content state is updated
      // Also allows onTextResponseChange callbacks to complete
      // CRITICAL: Wait longer to ensure React state updates have completed
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Use the latest content state - get it fresh from the ref (most up-to-date)
      // This ensures we have the most up-to-date data including table changes
      // Note: React state updates are asynchronous, so we use the ref which is updated synchronously
      const currentContent = contentRef.current || content; // Use ref first, fallback to state
      if (!currentContent) {
        console.error('[SAVE] No content available to save');
        return;
      }
      console.log('[SAVE] Using content from:', contentRef.current ? 'ref' : 'state');
      const rootKey = Object.keys(currentContent)[0];
      const root = currentContent[rootKey];
      const allResponses: Record<string, any> = {};
      
      console.log('[SAVE] Content structure:', {
        rootKey,
        rootType: Array.isArray(root) ? 'array' : typeof root,
        hasRoot: !!root
      });

      // Debug: Log all items with IDs to see what's being processed
      console.log('[SAVE] Starting extractResponses, content structure:', {
        rootKey,
        rootType: Array.isArray(root) ? 'array' : typeof root,
        rootKeys: Array.isArray(root) ? `array with ${root.length} items` : Object.keys(root || {})
      });

      // Extract all responses from all categories
      const extractResponses = (items: any[], categoryName: string) => {
        for (const item of items) {
          if (!item.id) continue; // Skip items without ID
          
          // Debug FRA-12 specifically
          if (item.id === "FRA-12") {
            console.log('[SAVE] Found FRA-12 in extractResponses:', {
              itemId: item.id,
              hasFra12InquiryTable: !!item.fra12InquiryTable,
              fra12InquiryTable: item.fra12InquiryTable,
              itemKeys: Object.keys(item)
            });
          }
          
          // CRITICAL: Check localStorage FIRST for textbox content (most reliable and up-to-date source)
          // This is checked BEFORE checking content state because state updates are asynchronous
          const textboxFromLocalStorage = localStorage.getItem(`textbox_${item.id}`);
          const hasTextboxContent = textboxFromLocalStorage && textboxFromLocalStorage.trim().length > 0;
          
          // Check if item has any data worth saving
          const hasAnswer = item.answer !== null && item.answer !== undefined && item.answer !== '';
          const hasDocuments = item.documents && (Array.isArray(item.documents) ? item.documents.length > 0 : true);
          const hasTextResponse = item.textResponse && (item.textResponse.html || item.textResponse.plainText);
          const hasTable = (item.id === "OC-4" && item.customerSupplierTable && Array.isArray(item.customerSupplierTable) && item.customerSupplierTable.length > 0) ||
                          (item.id === "FRA-5" && item.relatedPartiesTable && Array.isArray(item.relatedPartiesTable) && item.relatedPartiesTable.length > 0) ||
                          (item.id === "FRA-12" && item.fra12InquiryTable && Array.isArray(item.fra12InquiryTable) && item.fra12InquiryTable.length > 0) ||
                          (item.id === "PD-3" && item.pd3PersonnelTable && Array.isArray(item.pd3PersonnelTable) && item.pd3PersonnelTable.length > 0);
          
          // Debug logging for FRA-12
          if (item.id === "FRA-12") {
            console.log(`[SAVE] FRA-12 data check in extractResponses:`, {
              hasAnswer,
              hasDocuments,
              hasTextResponse,
              hasTextboxContent,
              hasTable,
              fra12InquiryTable: item.fra12InquiryTable,
              tableLength: item.fra12InquiryTable?.length || 0,
              willInclude: hasAnswer || hasDocuments || hasTextResponse || hasTextboxContent || hasTable
            });
          }
          
          // ALWAYS include item if it has textbox content in localStorage, even if no other data
          // This ensures textbox-only changes are saved on the first attempt
          if (hasAnswer || hasDocuments || hasTextResponse || hasTextboxContent || hasTable) {
            const responseData: any = {
              questionId: item.id,
              selectedOption: null,
              textResponse: null,
              documents: [],
              lastUpdated: new Date().toISOString(),
              companyName
            };

            // Handle different response types
            if (hasAnswer) {
              if (item.type === "radio" && item.options) {
                responseData.selectedOption = item.answer;
              } else if (item.type === "checkbox") {
                responseData.selectedOption = Array.isArray(item.answer) ? item.answer.join(", ") : item.answer;
              } else if (item.type === "date") {
                // Save date to selectedOption field
                responseData.selectedOption = item.answer;
              } else if (item.type === "text" || item.type === "textarea") {
                responseData.textResponse = {
                  html: item.answer || "",
                  plainText: item.answer || ""
                };
              }
            }

            if (hasDocuments) {
              const documents = Array.isArray(item.documents) ? item.documents : [item.documents];
              
              // Extract file names for documents field (excluding textbox)
              const fileDocuments = documents.filter((doc: any) => doc.type !== 'textbox');
              responseData.documents = fileDocuments.map((doc: any) => ({
                name: doc.name,
                type: doc.type,
                url: doc.url,
                reference: doc.reference
              }));
              
              // Extract textResponse from textbox documents - but localStorage takes priority
              const textboxDoc = documents.find((doc: any) => doc.type === 'textbox');
              if (textboxDoc && textboxDoc.content && !hasTextboxContent) {
                // Only use content state if localStorage doesn't have it
                responseData.textResponse = {
                  html: textboxDoc.content,
                  plainText: textboxDoc.content.replace(/<[^>]*>/g, '') // Strip HTML tags for plain text
                };
              }
            }

            // If textResponse exists directly on item (not from textbox), use it
            // But only if we haven't already set it from textbox AND localStorage doesn't have it
            if (hasTextResponse && !responseData.textResponse && !hasTextboxContent) {
              responseData.textResponse = item.textResponse;
            }
            
            // CRITICAL: Check localStorage FIRST for textbox content (highest priority)
            // This ensures textbox content is saved even if content state hasn't been updated yet
            // This is the MOST IMPORTANT check - it ensures textbox-only changes are captured on first save
            if (textboxFromLocalStorage) {
              responseData.textResponse = {
                html: textboxFromLocalStorage,
                plainText: textboxFromLocalStorage.replace(/<[^>]*>/g, '') // Strip HTML tags for plain text
              };
              console.log(`[SAVE] Found textbox content in localStorage for ${item.id}, length: ${textboxFromLocalStorage.length}, adding to response`);
            }

            // Handle OC-4 CustomerSupplierTable - add to responseData
            if (item.id === "OC-4" && item.customerSupplierTable && Array.isArray(item.customerSupplierTable) && item.customerSupplierTable.length > 0) {
              responseData.CustomerSupplierTable = {
                columns: [
                  { key: "typeOfRelationship", label: "Type of Relationship" },
                  { key: "nameOfParty", label: "Name of Party" },
                  { key: "whetherRelated", label: "Whether Related (Yes/No)" },
                  { key: "riskAssessed", label: "Risk Assessed (Lower/Higher/Significant)" }
                ],
                rows: item.customerSupplierTable
              };
              console.log(`[SAVE] Added CustomerSupplierTable to OC-4 response with ${item.customerSupplierTable.length} rows`);
            }

            // Handle OC-3 AccountingEstimateTable - add to responseData
            if (item.id === "OC-3" && item.accountingEstimateTable && Array.isArray(item.accountingEstimateTable) && item.accountingEstimateTable.length > 0) {
              responseData.accounting_estimate = {
                columns: [
                  { key: "accounting_estimate", label: "Accounting Estimate", type: "text" },
                  { key: "fs_line_item", label: "FS Line Item", type: "text" },
                  { key: "description", label: "Description", type: "text" }
                ],
                rows: item.accountingEstimateTable.map((row: any, index: number) => ({
                  row_id: `row_${index + 1}`,
                  accounting_estimate: row.accounting_estimate || "",
                  fs_line_item: row.fs_line_item || "",
                  description: row.description || ""
                }))
              };
              console.log(`[SAVE] Added accounting_estimate table to OC-3 response with ${item.accountingEstimateTable.length} rows`);
            }

            // Handle FRA-5 relatedPartiesTable - add to responseData
            if (item.id === "FRA-5" && item.relatedPartiesTable && Array.isArray(item.relatedPartiesTable) && item.relatedPartiesTable.length > 0) {
              responseData.relatedPartiesTable = {
                columns: [
                  { key: "role", label: "Role" },
                  { key: "name", label: "Name" },
                  { key: "position", label: "Position" },
                  { key: "date", label: "Date of Discussion" }
                ],
                rows: item.relatedPartiesTable
              };
              console.log(`[SAVE] Added relatedPartiesTable to FRA-5 response with ${item.relatedPartiesTable.length} rows`);
            }

            // Handle FRA-12 FRA12InquiryTable - add to responseData
            if (item.id === "FRA-12") {
              console.log(`[SAVE] Checking FRA-12 table data:`, {
                hasFra12InquiryTable: !!item.fra12InquiryTable,
                isArray: Array.isArray(item.fra12InquiryTable),
                length: item.fra12InquiryTable?.length || 0,
                tableData: item.fra12InquiryTable
              });
              if (item.fra12InquiryTable && Array.isArray(item.fra12InquiryTable) && item.fra12InquiryTable.length > 0) {
                responseData.FRA12InquiryTable = {
                  columns: [
                    { key: "role", label: "Role" },
                    { key: "name", label: "Name" },
                    { key: "position", label: "Position" },
                    { key: "date", label: "Date of Discussion" }
                  ],
                  rows: item.fra12InquiryTable
                };
                console.log(`[SAVE] Added FRA12InquiryTable to FRA-12 response with ${item.fra12InquiryTable.length} rows`);
              } else {
                console.log(`[SAVE] FRA-12 table data not found or empty`);
              }
            }

            // Handle PD-3 PD3PersonnelTable - add to responseData
            if (item.id === "PD-3" && item.pd3PersonnelTable && Array.isArray(item.pd3PersonnelTable) && item.pd3PersonnelTable.length > 0) {
              responseData.PD3PersonnelTable = {
                columns: [
                  { key: "nameOfPersonnel", label: "Name of Personnel" },
                  { key: "position", label: "Position" }
                ],
                rows: item.pd3PersonnelTable
              };
              console.log(`[SAVE] Added PD3PersonnelTable to PD-3 response with ${item.pd3PersonnelTable.length} rows`);
            }

            // Always add to allResponses if we have ANY data (answer, documents, textResponse, or table)
            // This ensures textbox-only responses and table-only responses are saved
            const hasTable = (item.id === "OC-4" && responseData.CustomerSupplierTable) || 
                            (item.id === "OC-3" && responseData.accounting_estimate) ||
                            (item.id === "FRA-5" && responseData.relatedPartiesTable) ||
                            (item.id === "FRA-12" && responseData.FRA12InquiryTable) ||
                            (item.id === "PD-3" && responseData.PD3PersonnelTable);
            if (responseData.selectedOption || responseData.textResponse || responseData.documents.length > 0 || hasTable) {
              allResponses[item.id] = responseData;
              console.log(`[SAVE] Added response for ${item.id}:`, {
                hasSelectedOption: !!responseData.selectedOption,
                hasTextResponse: !!responseData.textResponse,
                textResponseLength: responseData.textResponse?.html?.length || 0,
                hasDocuments: responseData.documents.length > 0,
                hasTable: !!hasTable,
                tableData: item.id === "FRA-12" ? responseData.FRA12InquiryTable : item.id === "FRA-5" ? responseData.relatedPartiesTable : item.id === "OC-4" ? responseData.CustomerSupplierTable : item.id === "OC-3" ? responseData.accounting_estimate : null
              });
            } else {
              console.log(`[SAVE] Skipping ${item.id} - no data to save`);
            }
          } else {
            // Log items that are being skipped
            console.log(`[SAVE] Skipping ${item.id} - no data found (no answer, documents, textResponse, or localStorage textbox)`);
          }

          // Recursively check nested questions
          if (item.questions && Array.isArray(item.questions)) {
            extractResponses(item.questions, categoryName);
          }
        }
      };

      // Process each category
      if (Array.isArray(root)) {
        extractResponses(root, 'main');
      } else {
        Object.keys(root).forEach(categoryName => {
          if (Array.isArray(root[categoryName])) {
            extractResponses(root[categoryName], categoryName);
          }
        });
      }

      // Before saving, also check for any textbox content in localStorage that might not be in content state
      // This is a final safety check to ensure we don't miss any textbox-only changes
      // This is CRITICAL for first-time saves where the content state might not be updated yet
      const allLocalStorageKeys = Object.keys(localStorage);
      const textboxKeys = allLocalStorageKeys.filter(key => key.startsWith('textbox_'));
      
      console.log(`[SAVE] Scanning localStorage for textbox content. Found ${textboxKeys.length} textbox keys`);
      
      for (const key of textboxKeys) {
        const itemId = key.replace('textbox_', '');
        const textboxContent = localStorage.getItem(key);
        
        // If we don't already have a response for this item, but it has textbox content, add it
        if (textboxContent && textboxContent.trim().length > 0) {
          if (!allResponses[itemId]) {
            console.log(`[SAVE] Found textbox content in localStorage for ${itemId} that wasn't in content state, adding to responses. Content length: ${textboxContent.length}`);
            allResponses[itemId] = {
              questionId: itemId,
              selectedOption: null,
              textResponse: {
                html: textboxContent,
                plainText: textboxContent.replace(/<[^>]*>/g, '')
              },
              documents: [],
              lastUpdated: new Date().toISOString(),
              companyName
            };
          } else {
            // Even if we have a response, make sure textResponse is set from localStorage (most up-to-date)
            if (!allResponses[itemId].textResponse) {
              console.log(`[SAVE] Updating existing response for ${itemId} with textbox content from localStorage`);
              allResponses[itemId].textResponse = {
                html: textboxContent,
                plainText: textboxContent.replace(/<[^>]*>/g, '')
              };
            } else {
              // Compare and use the longer/newer content (likely more up-to-date)
              const existingLength = allResponses[itemId].textResponse.html?.length || 0;
              if (textboxContent.length > existingLength) {
                console.log(`[SAVE] Updating response for ${itemId} with longer textbox content from localStorage (${textboxContent.length} vs ${existingLength})`);
                allResponses[itemId].textResponse = {
                  html: textboxContent,
                  plainText: textboxContent.replace(/<[^>]*>/g, '')
                };
              }
            }
          }
        }
      }
      
      // Save all responses to SharePoint
      console.log('=== SAVING TO SHAREPOINT ===');
      console.log('Saving to SharePoint:', {
        companyName,
        sectionKey,
        responseCount: Object.keys(allResponses).length,
        responseIds: Object.keys(allResponses),
        responses: allResponses
      });
      
      // Debug: Check if FRA-12 table data is in allResponses
      if (allResponses['FRA-12']) {
        console.log('[SAVE]  FRA-12 response being saved:', {
          hasTable: !!allResponses['FRA-12'].FRA12InquiryTable,
          tableData: allResponses['FRA-12'].FRA12InquiryTable,
          fullResponse: allResponses['FRA-12']
        });
      } else {
        console.log('[SAVE]  WARNING: FRA-12 not found in allResponses');
        console.log('[SAVE] Available response IDs:', Object.keys(allResponses));
      }
      
      if (window.planningAPI?.saveAllResponses) {
        const result = await window.planningAPI.saveAllResponses({
          companyName,
          sectionKey,
          responses: allResponses
        });
        console.log('SharePoint save result:', result);
      } else {
        console.error('planningAPI.saveAllResponses not available');
      }

      setSharePointSaveStatus('saved');
      setTimeout(() => setSharePointSaveStatus('idle'), 3000); // Reset status after 3 seconds
    } catch (error) {
      console.error("Failed to save responses to SharePoint:", error);
      setSharePointSaveStatus('error');
      setSharePointSaveError(error instanceof Error ? error.message : 'Unknown error');
    }
  };

  function buildAnswersMap(items: any[]): Record<string, any> {
    const map: Record<string, any> = {};
    const walk = (arr: any[]) => {
      for (const el of arr) {
        if (el && typeof el === "object") {
          if (el.id) map[el.id] = el.answer;
          if (Array.isArray(el.questions)) walk(el.questions);
        }
      }
    };
    walk(items);
    return map;
  }

  // Build answers map from ALL sections, not just current tab
  function buildAllAnswersMap(): Record<string, any> {
    const map: Record<string, any> = {};
    if (!content) return map;
    
    const walk = (obj: any) => {
      if (Array.isArray(obj)) {
        obj.forEach(walk);
      } else if (obj && typeof obj === "object") {
        if (obj.id && obj.answer !== undefined) {
          map[obj.id] = obj.answer;
        }
        // Walk through all properties
        Object.values(obj).forEach(value => {
          if (Array.isArray(value) || (value && typeof value === "object")) {
            walk(value);
          }
        });
      }
    };
    
    walk(content);
    return map;
  }

  function evalCondition(cond: any, answers: Record<string, any>): boolean {
    if (!cond) return true;
    const { questionId, operator, value } = cond;
    const actual = answers[questionId];
    switch (operator) {
      case "==":
      default:
        return actual === value;
    }
  }

  function evalConditions(
    conds: any[] | undefined,
    answers: Record<string, any>,
  ): boolean {
    if (!conds || conds.length === 0) return true;
    return conds.every((c) => evalCondition(c, answers));
  }

  if (loading) {
    return <div className="text-white/70">Loading {title}…</div>;
  }
  if (!content) {
    return <div className="text-white/70">No content</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white">{title}</h2>
        <div className="flex items-center gap-3">
          {lastSaved && sectionKey !== "engagement-acceptance" && (
            <Badge className="bg-emerald-500/15 text-emerald-300">
              Saved {lastSaved.toLocaleTimeString()}
            </Badge>
          )}
        </div>
      </div>
      <Card className="border-white/10 bg-black/30 text-white">
        <CardHeader className="sticky top-0 z-10 bg-black/30 backdrop-blur">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">
              {Object.keys(content || {})[0]}
            </CardTitle>
            <div className="flex items-center gap-4">
              {activeTab &&
                (() => {
                  const p = computeTabProgress(activeTab);
                  const percent = p.total
                    ? Math.round((p.answered / p.total) * 100)
                    : 0;
                  return (
                    <div className="text-xs text-white/70">
                      {p.answered}/{p.total} ({percent}%)
                    </div>
                  );
                })()}
              <div className="flex items-center gap-2">
                {!isEditMode ? (
                  <Button
                    onClick={() => {
                      if (!isArchivalMode) {
                        // Use startTransition to mark this as non-urgent update
                        startTransition(() => {
                          setIsEditMode(true);
                        });
                      }
                    }}
                    variant="outline"
                    size="sm"
                    disabled={isArchivalMode}
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    Edit
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={async () => {
                        setSharePointSaveStatus('saving');
                        try {
                          await saveAllResponsesToSharePoint();
                          setSharePointSaveStatus('saved');
                          setIsEditMode(false);
                        } catch (error) {
                          setSharePointSaveStatus('error');
                          setSharePointSaveError(error instanceof Error ? error.message : 'Save failed');
                        }
                      }}
                      disabled={sharePointSaveStatus === 'saving'}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {sharePointSaveStatus === 'saving' ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      onClick={() => setShowStopEditingDialog(true)}
                      variant="outline"
                      size="sm"
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      Stop Editing
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList
              className={
                sectionKey === "fraud-risk" ||
                sectionKey === "engagement-acceptance" ||
                sectionKey === "understanding-entity"
                  ? "flex flex-nowrap gap-3 justify-start mb-4 w-full overflow-x-auto overflow-y-visible scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent p-3"
                  : undefined
              }
            >
              {tabs.map((t) => {
                const p = computeTabProgress(t.key);
                const isWrappedSection =
                  sectionKey === "fraud-risk" ||
                  sectionKey === "engagement-acceptance" ||
                  sectionKey === "understanding-entity";
                const triggerClasses = isWrappedSection
                  ? "gap-2 flex-none justify-center whitespace-nowrap min-w-fit"
                  : "gap-2";
                return (
                  <TabsTrigger key={t.key} value={t.key} className={triggerClasses}>
                    <span>{t.title}</span>
                    <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] text-white/80">
                      {p.answered}/{p.total}
                    </span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
            {tabs.map((t) => {
              const answersMap = buildAllAnswersMap(); // Use all answers, not just current tab
              const renderItems = (items: any[]): React.ReactNode =>
                items.map((item: any) => {
                  // Group-level conditional wrapper
                  if (
                    item &&
                    typeof item === "object" &&
                    item.condition &&
                    Array.isArray(item.questions)
                  ) {
                    if (!evalCondition(item.condition, answersMap)) return null;
                    return (
                      <React.Fragment key={`cond-${Math.random()}`}>
                        {renderItems(item.questions)}
                      </React.Fragment>
                    );
                  }
                  if (
                    item &&
                    typeof item === "object" &&
                    Array.isArray(item.conditions)
                  ) {
                    if (!evalConditions(item.conditions, answersMap))
                      return null;
                  }
                  if (
                    item &&
                    typeof item === "object" &&
                    item.condition &&
                    !item.questions
                  ) {
                    if (!evalCondition(item.condition, answersMap)) return null;
                  }

                  const drqIf = item.documentRequiredIf;
                  let requiredNote = Boolean(item.documentRequired);
                  if (Array.isArray(drqIf)) {
                    requiredNote = requiredNote || drqIf.includes(item.answer);
                  } else if (typeof drqIf !== "undefined") {
                    requiredNote = requiredNote || item.answer === drqIf;
                  }

                  const noteIf =
                    item.noteIf && item.answer && item.noteIf[item.answer];

                  // Sub-question logic
                  let subQuestion: any | null = null;
                  if (
                    item.subQuestionIf &&
                    item.answer &&
                    item.subQuestionIf[item.answer]
                  ) {
                    subQuestion = {
                      ...item.subQuestionIf[item.answer],
                      parentId: item.id,
                    };
                  }
                  const showDynTable = (() => {
                    const dt = item.dynamicTableIf;
                    if (!dt) {
                      if (item.id === "OC-3") {
                        console.log(`[OC-3] No dynamicTableIf found`);
                      }
                      return false;
                    }
                    if (typeof dt === "string") {
                      const result = String(item.answer) === String(dt);
                      if (item.id === "OC-3") {
                        console.log(`[OC-3] showDynTable check:`, {
                          dynamicTableIf: dt,
                          answer: item.answer,
                          answerString: String(item.answer),
                          match: result,
                          typeOfAnswer: typeof item.answer,
                          willShow: result
                        });
                      }
                      return result;
                    }
                    if (typeof dt === "object") return Boolean(dt[item.answer]);
                    return false;
                  })();
                  const showTableIf = (() => {
                    const ti = item.tableif;
                    if (!ti) return false;
                    if (typeof ti === "string") return item.answer === ti;
                    if (typeof ti === "object") return Boolean(ti[item.answer]);
                    return false;
                  })();
                  // For document type with table flag, always show table (deprecated - now using type: "table")
                  const showDocumentTable = item.type === "document" && item.table === true;

                  const matchesSearch = (txt: string) =>
                    txt.toLowerCase().includes(searchQuery.toLowerCase());
                  const isAnswered = isItemAnswered(item);
                  const visibleBySearch =
                    !searchQuery ||
                    matchesSearch(String(item.question || item.id || ""));
                  const visibleByFilter = !onlyUnanswered || !isAnswered;
                  if (!visibleBySearch || !visibleByFilter) return null;

                  return (
                    <div
                      key={item.id ?? item.head ?? Math.random()}
                      ref={(el) => {
                        if (item.id) questionRefs.current[item.id] = el;
                      }}
                      className="rounded-lg border border-white/10 bg-black/20 p-4"
                    >
                      {item.head ? (
                        <div className="mb-1 text-sm text-white/70">
                          {item.head}
                        </div>
                      ) : (
                        <>
                          <div className="mb-2 text-sm font-medium text-white whitespace-pre-line flex items-start gap-2 relative">
                            <span className="flex-1">{item.question || item.id}</span>
                            <div className="flex items-center gap-1">
                              {item.id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setReviewCommentState({
                                    questionId: item.id,
                                    questionText: item.question || item.id,
                                    workspace: title,
                                    subPhase: t.title,
                                  })}
                                  className="h-6 w-6 p-0 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                                  title="Review Comment"
                                >
                                  <MessageSquare className="h-4 w-4" />
                                </Button>
                              )}
                              {item.tooltip && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 text-white/60 hover:text-white hover:bg-white/10"
                                      >
                                        <Lightbulb className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent 
                                      side="top" 
                                      className="max-w-md p-3 bg-gray-900 text-white border border-gray-700"
                                    >
                                      <p className="text-sm">{item.tooltip}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          </div>
                          {noteIf && (
                            <div className="mb-2 text-xs text-white/70">
                              {noteIf}
                            </div>
                          )}
                          <FieldRenderer
                            item={item}
                            value={item.answer}
                            onChange={(val) =>
                              setItemAnswer(t.key, item.id, val)
                            }
                            sectionKey={sectionKey}
                            groupKey={t.key}
                            disabled={!isEditMode}
                            isEditMode={isEditMode}
                            setItemField={setItemField}
                          />
                          
                          {/* Document Manager for questions requiring documentation */}
                          {requiredNote && item.type !== "document" && (
                            <div className="mt-3">
                              <DocumentManager
                                sectionKey={String(sectionKey)}
                                groupKey={t.key}
                                itemId={String(item.id)}
                                itemQuestion={String(item.question || "")}
                                itemTextResponse={item.textResponse}
                                itemDocuments={item.documents}
                                disabled={!isEditMode}
                                onDocumentsChange={(documents) => {
                                  // Update the item's documents field
                                  setItemField(t.key, item.id, "documents", documents);
                                }}
                                onTextResponseChange={(textResponse) => {
                                  // Update the item's textResponse field
                                  setItemField(t.key, item.id, "textResponse", textResponse);
                                }}
                              />
                            </div>
                          )}
                          {subQuestion && (
                            <div className="mt-3 rounded-md border border-white/10 bg-black/30 p-3">
                              <div className="mb-2 text-xs font-medium text-white/80">
                                {subQuestion.question}
                              </div>
                              <FieldRenderer
                                item={subQuestion}
                                value={item.subAnswers?.[subQuestion.id]}
                                onChange={(val) =>
                                  setItemField(
                                    t.key,
                                    item.id,
                                    "subAnswers",
                                    { [subQuestion.id]: val },
                                    true,
                                  )
                                }
                                sectionKey={sectionKey}
                                groupKey={t.key}
                                disabled={!isEditMode}
                                isEditMode={isEditMode}
                                setItemField={setItemField}
                              />
                              {subQuestion.tableif &&
                                ((typeof subQuestion.tableif === "string" &&
                                  item.subAnswers?.[subQuestion.id] ===
                                    subQuestion.tableif) ||
                                  (typeof subQuestion.tableif === "object" &&
                                    subQuestion.tableif[
                                      item.subAnswers?.[subQuestion.id]
                                    ])) && (
                                  <div className="mt-3 rounded-md border border-white/10 bg-black/20 p-3">
                                    <div className="mb-2 text-xs font-medium text-white/80">
                                      Details
                                    </div>
                                    <SimpleTableEditor
                                      rows={
                                        item.subAnswers?.[
                                          `${subQuestion.id}__table`
                                        ]
                                      }
                                      onChange={(val) =>
                                        setItemField(
                                          t.key,
                                          item.id,
                                          "subAnswers",
                                          { [`${subQuestion.id}__table`]: val },
                                          true,
                                        )
                                      }
                                    />
                                  </div>
                                )}
                            </div>
                          )}
                          {(showDynTable || showTableIf) && (
                            <div className="mt-3 rounded-md border border-white/10 bg-black/30 p-3">
                              <div className="mb-2 text-xs font-medium text-white/80">
                                Details
                              </div>
                              {item.id === "OC-4" ? (
                                <CustomerSupplierTable
                                  value={item.customerSupplierTable}
                                  onChange={(val) =>
                                    setItemField(t.key, item.id, "customerSupplierTable", val)
                                  }
                                  disabled={!isEditMode}
                                />
                              ) : item.id === "OC-3" ? (
                                (() => {
                                  console.log(`[OC-3] Rendering AccountingEstimateTable:`, {
                                    hasTable: !!item.accountingEstimateTable,
                                    tableValue: item.accountingEstimateTable,
                                    showDynTable,
                                    answer: item.answer
                                  });
                                  return (
                                    <AccountingEstimateTable
                                      value={item.accountingEstimateTable}
                                      onChange={(val) =>
                                        setItemField(t.key, item.id, "accountingEstimateTable", val)
                                      }
                                      disabled={!isEditMode}
                                    />
                                  );
                                })()
                              ) : (
                                <SimpleTableEditor
                                  rows={item.details}
                                  onChange={(val) =>
                                    setItemField(t.key, item.id, "details", val)
                                  }
                                />
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                });

              const p = computeTabProgress(t.key);
              return (
                <TabsContent key={t.key} value={t.key} className="mt-4">
                  <div className="space-y-6">
                    <div className="space-y-4">
                      {renderItems(t.items || [])}
                    </div>
                    <div className="mt-2 text-right text-[10px] text-white/60">
                      {p.answered}/{p.total} completed
                    </div>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </CardContent>
      </Card>
      
      {/* Stop Editing Confirmation Dialog */}
      <ConfirmationDialog
        open={showStopEditingDialog}
        onOpenChange={setShowStopEditingDialog}
        title="Stop Editing"
        description="Do you want to save your changes before stopping editing?"
        onYes={async () => {
          setShowStopEditingDialog(false);
          setSharePointSaveStatus('saving');
          try {
            await saveAllResponsesToSharePoint();
            setSharePointSaveStatus('saved');
            setIsEditMode(false);
          } catch (error) {
            setSharePointSaveStatus('error');
            setSharePointSaveError(error instanceof Error ? error.message : 'Save failed');
            alert("Failed to save changes. Please try again.");
          }
        }}
        onNo={() => {
          setShowStopEditingDialog(false);
          setIsEditMode(false);
        }}
        onCancel={() => {
          setShowStopEditingDialog(false);
        }}
        yesText="Yes"
        noText="No"
        cancelText="Cancel"
      />
      
      {/* Review Comment Modal */}
      {reviewCommentState && (
        <ReviewComment
          questionId={reviewCommentState.questionId}
          questionText={reviewCommentState.questionText}
          isOpen={true}
          onClose={() => setReviewCommentState(null)}
          workspace={reviewCommentState.workspace}
          subPhase={reviewCommentState.subPhase}
        />
      )}
    </div>
  );
}
